import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { authAPI } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// Full-size off-road truck icon and Colorado backcountry background
const OFFROAD_ICON_URL = "https://static.prod-images.emergentagent.com/jobs/60aaf568-1e1a-4527-97bf-fc896d1bd3d2/images/04537aaf8f60abd171c931c58da5cd76c22af29f5769fcb343613da7c61527e0.png";
const COLORADO_BG_URL = "https://static.prod-images.emergentagent.com/jobs/60aaf568-1e1a-4527-97bf-fc896d1bd3d2/images/cd6bcd418855ebeade7ab7b7256dfe10e8b4fce65cb376506a85b67468594fd3.png";

export default function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await authAPI.register(form);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      toast.success("Account created successfully!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Colorado Rockies Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${COLORADO_BG_URL}')`,
        }}
      />
      {/* Gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/80" />

      <Card className="w-full max-w-md relative z-10 card-tactical bg-zinc-950/90 border-zinc-800 backdrop-blur-sm">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-20 h-20 bg-amber-500/10 rounded-lg flex items-center justify-center border border-amber-500/30 p-2">
            <img 
              src={OFFROAD_ICON_URL} 
              alt="Off-Road Truck" 
              className="w-full h-full object-contain"
            />
          </div>
          <CardTitle className="text-2xl">
            <span className="text-amber-500 font-bold">Dynamite Off-Road</span>
            <span className="block text-base text-zinc-300 font-medium tracking-wide mt-1">
              Create Your Account
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm text-muted-foreground">
                Name
              </Label>
              <Input
                id="name"
                type="text"
                data-testid="register-name-input"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="bg-white text-gray-900 border-emerald-200 focus:border-primary placeholder:text-gray-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="register-email-input"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="bg-white text-gray-900 border-emerald-200 focus:border-primary placeholder:text-gray-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm text-muted-foreground">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="register-password-input"
                placeholder="Min 6 characters"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="bg-white text-gray-900 border-emerald-200 focus:border-primary placeholder:text-gray-400"
              />
            </div>
            <Button
              type="submit"
              data-testid="register-submit-btn"
              className="w-full btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="login-link"
              className="text-primary hover:underline font-medium"
            >
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
